import { FIRM, FIRM_ADDRESS, MICHAEL_PHONE, PENINSULA_ADDRESS } from "@/lib/firm";

export const WILL_FEES = {
  will: "from $660",
  epoa: "from $440",
  pack: "$825",
  couples: "$1,499",
} as const;

export type WillsLocation = {
  slug: string;
  name: string;
  title: string;
  metaTitle: string;
  description: string;
  lead: string;
  paragraphs: string[];
  points: { t: string; d: string }[];
  nearby: string[];
  postcodes: string[];
  faqs: { q: string; a: string }[];
  office: "coast" | "peninsula";
};

const FEE_FAQ = (name: string) => ({
  q: `How much does a will cost in ${name}?`,
  a: `A simple will starts ${WILL_FEES.will} including GST, an enduring power of attorney is ${WILL_FEES.epoa}, the will and enduring power of attorney pack is ${WILL_FEES.pack}, and a couples pack is ${WILL_FEES.couples}. Wills involving blended families, companies, trusts or a self-managed super fund are quoted separately in writing before we start.`,
});

const HOME_VISIT_FAQ = (name: string, office: "coast" | "peninsula") => ({
  q: `Can you come to me in ${name}?`,
  a:
    office === "peninsula"
      ? `Yes. Michael Klein lives on the Redcliffe peninsula and our office is at ${PENINSULA_ADDRESS}. He sees clients at home, in hospital and in retirement village and aged care units across ${name} and the surrounding suburbs, at no extra charge for a local visit.`
      : `Yes. Erin Wilkinson lives at Golden Beach and regularly sees clients at home, in hospital or in an aged care or retirement village unit across ${name} and the surrounding 4551 suburbs. There is no extra charge for a local home visit.`,
});


const EPOA_FAQ = {
  q: "Do I need an enduring power of attorney as well as a will?",
  a: "They do different jobs. A will operates after death; an enduring power of attorney lets someone deal with your finances and, if you choose, your personal and health matters while you are alive but unable to decide for yourself. Without one, your family has to apply to QCAT, which takes months and costs far more than the document would have.",
};

const CUSTODY_FAQ = {
  q: "Where is the original will kept?",
  a: "We hold originals in safe custody free of charge and give you a copy for your records. That matters more than people expect — an original that cannot be found, or one that has been stapled, pinned or torn, creates an affidavit problem for your executor at exactly the wrong moment.",
};

const CONTEST_FAQ = {
  q: "Can my will be challenged?",
  a: "In Queensland an eligible person — a spouse, child or dependant — can bring a family provision application within nine months of death. A will cannot make that impossible, but the way it is drafted, and the file note that sits behind it, makes a claim much harder to run. We ask the awkward questions at the time we take instructions.",
};

function loc(
  slug: string,
  name: string,
  title: string,
  lead: string,
  paragraphs: string[],
  points: { t: string; d: string }[],
  nearby: string[],
  postcodes: string[],
  faqs: { q: string; a: string }[],
  office: "coast" | "peninsula" = "coast",
): WillsLocation {
  return {
    slug,
    name,
    title,
    metaTitle: `Wills & Estate Lawyers ${name} QLD — Wills ${WILL_FEES.will}`,
    description: `Wills, enduring powers of attorney and estate advice for ${name} families. Simple will ${WILL_FEES.will}, will and EPOA pack ${WILL_FEES.pack}, incl. GST. Home visits available. Call ${office === "peninsula" ? MICHAEL_PHONE : FIRM.phone}.`,
    lead,
    paragraphs,
    points,
    nearby,
    postcodes,
    office,
    faqs: [...faqs, FEE_FAQ(name), EPOA_FAQ, HOME_VISIT_FAQ(name, office), CUSTODY_FAQ, CONTEST_FAQ],
  };
}


export const WILLS_LOCATIONS: WillsLocation[] = [
  loc(
    "golden-beach",
    "Golden Beach",
    "Wills and estate lawyers at Golden Beach",
    `Our office is at ${FIRM_ADDRESS} — a few streets from most of the suburb. Simple wills ${WILL_FEES.will}, will and enduring power of attorney pack ${WILL_FEES.pack}, both including GST.`,
    [
      "Golden Beach is a long-tenure suburb. Most of the wills we are asked to review here were made a long time ago, often when the children were young and the house was worth a fraction of what it is now. The executor named in a 1990s will is frequently someone who has since died, moved overseas or fallen out with the family.",
      "The other recurring issue is the title. Where one owner died years ago and nothing was ever done, the survivor's will cannot deal cleanly with a property that still shows two names. We check the title at the same time as we take will instructions, because it costs nothing to look and it saves the estate a separate application later.",
      "Erin Wilkinson lives in Golden Beach and does the will and enduring power of attorney appointments locally, including at home if that is easier.",
      "Looking for a family lawyer in Golden Beach or Pelican Waters? We don't act in separation, divorce or parenting matters. Where we help families is with the planning around them: updating a will after separation or a new relationship, protecting children from an earlier relationship, enduring powers of attorney, and estate disputes after a death. If you need a family law specialist, call us and we will point you to one.",
    ],
    [
      {
        t: "Old wills reviewed, not just replaced",
        d: "We read what you already have, tell you whether it still works and what has changed in your circumstances. Sometimes a codicil is enough; more often a fresh will is cheaper than fixing one.",
      },
      {
        t: "Property still in two names",
        d: "Where a spouse died years ago and the title was never updated, we deal with the survivorship application alongside the new will.",
      },
      {
        t: "Home and bedside appointments",
        d: "Signing at home, in hospital or at an aged care facility, with the capacity and undue influence questions properly documented on the file.",
      },
    ],
    ["Caloundra", "Pelican Waters", "Bulcock Beach", "Aroona", "Currimundi", "Baringa"],
    ["4551"],
    [
      {
        q: "Can I sign my will at your Theresa Street office?",
        a: "Yes. Golden Beach is where our Sunshine Coast office sits, at 40 Theresa Street, and most local clients sign there. If getting out is difficult we come to you instead.",
      },
    ],
  ),
  loc(
    "caloundra",
    "Caloundra",
    "Wills and estate lawyers in Caloundra",
    `Fixed-fee wills and enduring powers of attorney for Caloundra families — ${WILL_FEES.will} for a simple will, ${WILL_FEES.pack} for the will and EPOA pack, ${WILL_FEES.couples} for a couples pack, including GST.`,
    [
      "Caloundra estate planning splits fairly neatly into two groups. Retirees who moved here and downsized into a unit or a village, and working families in Little Mountain, Currimundi and Battery Hill with a mortgage, superannuation and young children who need a guardian named.",
      "For the downsizers, the questions are about the unit, the village contract and the aged care deposit. Those assets do not all pass under the will in the way people assume, and a village exit entitlement is a contractual right rather than an asset the executor can simply sell.",
      "For the younger families, the largest asset is usually superannuation with insurance attached, and superannuation is not governed by the will unless you direct it there. That is a five-minute conversation that changes where a large sum of money ends up.",
    ],
    [
      {
        t: "Retirement village and over-50s residents",
        d: "We read the residence contract so the will and the estate plan reflect what the exit entitlement actually is, and when it is payable.",
      },
      {
        t: "Superannuation and death benefit nominations",
        d: "Binding nominations checked against the will so the biggest asset does not end up somewhere nobody intended.",
      },
      {
        t: "Guardianship for young children",
        d: "Testamentary guardians named, and the money held sensibly until the children are old enough to receive it.",
      },
    ],
    ["Golden Beach", "Kings Beach", "Moffat Beach", "Currimundi", "Little Mountain", "Battery Hill"],
    ["4551", "4551 Little Mountain", "4551 Currimundi"],
    [
      {
        q: "Do you see clients in Caloundra itself?",
        a: "We are five minutes away at 40 Theresa Street, Golden Beach, and we visit Caloundra homes, hospitals and village units regularly. Most clients find the local appointment easier than the drive to Maroochydore or Brisbane.",
      },
    ],
  ),
  loc(
    "pelican-waters",
    "Pelican Waters",
    "Wills and estate lawyers at Pelican Waters",
    `Estate planning for Pelican Waters owners — canal-front homes, self-managed super and family companies. Simple will ${WILL_FEES.will}, will and EPOA pack ${WILL_FEES.pack}, including GST.`,
    [
      "Pelican Waters wills are rarely the simplest ones we do. A canal-front or golf-course home, an investment property or two, sometimes a self-managed super fund and often a family trust or a company that still holds something.",
      "None of those assets pass under a will by default. A trust is controlled by whoever holds the appointor role, a company by whoever holds the shares, and superannuation by the fund trustee. An estate plan that only deals with what is in your personal name leaves the largest pieces to chance.",
      "Where the estate warrants it, a testamentary trust will can protect an inheritance from a beneficiary's relationship breakdown or business risk, and can be considerably more tax-effective for children under 18. We will tell you plainly if your situation does not warrant one.",
    ],
    [
      {
        t: "Trusts, companies and SMSFs",
        d: "Appointor succession, share transfers and SMSF control mapped alongside the will, so the structure and the estate plan agree with each other.",
      },
      {
        t: "Testamentary trust wills",
        d: "Asset protection and tax planning for larger estates, quoted before we start rather than assumed to be necessary.",
      },
      {
        t: "Canal and waterfront titles",
        d: "Revetment obligations, easements and community title interests identified so the executor is not discovering them at sale.",
      },
    ],
    ["Golden Beach", "Caloundra", "Bells Creek", "Aroona", "Baringa", "Little Mountain"],
    ["4551"],
    [
      {
        q: "I have a family trust — does my will deal with it?",
        a: "Not on its own. The trust's assets are not yours to give away; what matters is who takes over the appointor and trustee roles when you die, and that is governed by the trust deed. We read the deed and deal with the succession there as well as in the will.",
      },
    ],
  ),
  loc(
    "baringa",
    "Baringa",
    "Wills and estate lawyers at Baringa and Aura",
    `Wills, enduring powers of attorney and guardianship advice for Baringa, Aura and Nirimba families. Simple will ${WILL_FEES.will}, couples pack ${WILL_FEES.couples}, including GST.`,
    [
      "Baringa and the wider Aura estate are young suburbs, and the estate planning that suits them looks different from the coast. A mortgage, life insurance inside superannuation, a first or second child, and parents who may have helped with the deposit.",
      "Where parents have contributed to a deposit, whether that money was a gift or a loan matters enormously — to the estate, and to the child if the relationship ends. Documenting it at the time is straightforward. Reconstructing it years later, in front of a court, is not.",
      "Naming a guardian for young children, and deciding at what age an inheritance is actually handed over, are the two decisions parents in these suburbs most often leave undone. Both take one appointment.",
    ],
    [
      {
        t: "Young families and guardianship",
        d: "Testamentary guardians appointed and staged inheritance ages set, so an 18-year-old does not receive a life insurance payout in one lump.",
      },
      {
        t: "Parental deposit assistance",
        d: "Loan documentation and mortgage protection where parents helped with the purchase, so the contribution survives a separation or an estate claim.",
      },
      {
        t: "Superannuation and insurance",
        d: "Binding death benefit nominations aligned with the will, because for most Aura families that is the largest amount of money in play.",
      },
    ],
    ["Aura", "Nirimba", "Bells Creek", "Pelican Waters", "Caloundra West", "Golden Beach"],
    ["4551 Baringa", "4551 Nirimba", "4551 Bells Creek"],
    [
      {
        q: "We are in our thirties with a mortgage — is a will worth it yet?",
        a: "Yes, and more so than for a retiree. Superannuation and life insurance mean a young family's estate is often larger than the family thinks, and without a will there is no guardian named for the children and no control over when they receive the money.",
      },
    ],
  ),
  loc(
    "aura",
    "Aura",
    "Wills and estate lawyers for Aura",
    `Wills, enduring powers of attorney and guardianship advice for families across Aura — Baringa, Nirimba, Banya and Bells Creek. Simple will ${WILL_FEES.will}, couples pack ${WILL_FEES.couples}, including GST.`,
    [
      "Aura is being built for around fifty thousand people, and the estate planning that suits it is not the estate planning that suits the older coastal suburbs. The people we see here are typically in their late twenties to forties, with a mortgage, a young family, superannuation carrying default life insurance, and no will at all.",
      "For most Aura households the largest sum of money is not the house — it is the superannuation death benefit and the insurance attached to it. That money does not automatically follow the will. It is paid at the trustee's discretion unless there is a valid binding death benefit nomination in place, and those lapse after three years unless the fund offers a non-lapsing form. Aligning the nomination with the will is usually the single most valuable thing we do in the appointment.",
      "The other two decisions that get left undone are naming a guardian for young children and setting the age at which a child actually receives their inheritance. Without a will, both default to the legislation: no guardian is named, and the money is handed over at eighteen. Very few parents choose that when they are asked directly.",
      "Where parents helped with the deposit — common across Aura's house and land buyers — whether that money was a gift or a loan should be documented at the time. Reconstructing it later, after a separation or a death, is expensive and often unsuccessful.",
    ],
    [
      {
        t: "Superannuation and life insurance",
        d: "Binding death benefit nominations checked, renewed and aligned with the will, because for most Aura families that is the largest amount of money in play and it sits outside the estate by default.",
      },
      {
        t: "Guardianship for young children",
        d: "Testamentary guardians appointed and staged inheritance ages set, so a life insurance payout does not land on an eighteen-year-old in one lump.",
      },
      {
        t: "Mortgage and first home",
        d: "How the mortgage is dealt with on death, whether insurance covers it, and how a jointly owned home passes — set out plainly rather than assumed.",
      },
      {
        t: "Parental deposit assistance",
        d: "Gift or loan documented at the time, with mortgage protection where parents contributed, so the money survives a separation or an estate claim.",
      },
    ],
    ["Baringa", "Nirimba", "Banya", "Bells Creek", "Caloundra West", "Pelican Waters"],
    ["4551 Baringa", "4551 Nirimba", "4551 Banya", "4551 Bells Creek"],
    [
      {
        q: "We are young with a mortgage and not much else — do we need a will?",
        a: "More than you would think. Once superannuation and its default insurance are counted, a young Aura family's estate is frequently several hundred thousand dollars or more. Without a will there is no guardian named for the children, no control over when they receive that money, and someone has to apply to the court to administer the estate at a point when your family is least equipped to deal with it.",
      },
      {
        q: "Does my will cover my superannuation?",
        a: "Not by default. Superannuation is held by the fund trustee and paid under the fund's rules, not the will, unless a valid binding death benefit nomination directs it to your legal personal representative or a dependant. Many nominations lapse after three years without anyone noticing. We check yours and make sure it does what the will assumes it does.",
      },
      {
        q: "Can we do our wills together as a couple?",
        a: `Yes, and it is usually cheaper — a couples pack is ${WILL_FEES.couples} including GST. We take instructions from each of you, and if your wishes differ we say so rather than papering over it. Mirror wills are common for young Aura families, but they are not the right answer for everyone, particularly where there are children from an earlier relationship.`,
      },
    ],
  ),
  loc(
    "currimundi",

    "Currimundi",
    "Wills and estate lawyers at Currimundi and Little Mountain",
    `Local wills, enduring powers of attorney and elder law advice for Currimundi, Little Mountain, Aroona and Battery Hill. Simple will ${WILL_FEES.will}, will and EPOA pack ${WILL_FEES.pack}, including GST.`,
    [
      "Currimundi and Little Mountain sit between the retiree suburbs and the newer family estates, and the wills reflect it: second marriages, adult children from an earlier relationship and a house that one partner brought into the relationship.",
      "Blended families are the single most common reason a Queensland will is challenged. Leaving everything to a new spouse and trusting that the children will be looked after later is the arrangement that most reliably ends up in a family provision application. There are drafting answers to that, including life interests and testamentary trusts.",
      "We also see a steady stream of enduring power of attorney work here — often prompted by a parent's diagnosis, and often just late enough to be difficult. Capacity is assessed at the time of signing, so the earlier the conversation happens, the more options remain.",
    ],
    [
      {
        t: "Blended family wills",
        d: "Life interests, right-to-reside arrangements and testamentary trusts that provide for a new spouse without disinheriting the children.",
      },
      {
        t: "Enduring powers of attorney and capacity",
        d: "Documented capacity assessment, and honest advice when the window has closed and a QCAT application is the remaining path.",
      },
      {
        t: "Elder law and family agreements",
        d: "Granny flat arrangements and family loans documented properly, before they become an estate dispute.",
      },
    ],
    ["Little Mountain", "Aroona", "Battery Hill", "Caloundra", "Dicky Beach", "Golden Beach"],
    ["4551"],
    [
      {
        q: "My mother is starting to lose capacity — is it too late for a will?",
        a: "It depends on the level of capacity at the moment of signing, and that is a legal test rather than a medical label. We assess it, document it and sometimes obtain a specialist opinion. If capacity has gone, the alternatives are a QCAT-appointed administrator or, in limited cases, a statutory will application.",
      },
    ],
  ),
  loc(
    "bribie-island",
    "Bribie Island",
    "Wills and estate lawyers for Bribie Island",
    `Wills, enduring powers of attorney and elder law for Bongaree, Bellara, Woorim and Banksia Beach. Simple will ${WILL_FEES.will}, will and EPOA pack ${WILL_FEES.pack}, including GST.`,
    [
      "Bribie has one of the oldest populations in South East Queensland, and the estate planning questions here are practical ones: who deals with my money if I cannot, what happens to the house, and how the aged care accommodation deposit is treated.",
      "An enduring power of attorney is the document that matters most on Bribie and the one most often missing. Without it, a family facing a sudden decline has to apply to QCAT for an administrator, which takes months at the moment the money is most needed.",
      "We act for Bribie clients from Caloundra, about forty minutes up the road, and visit for signing rather than asking people to travel.",
    ],
    [
      {
        t: "Aged care and accommodation deposits",
        d: "How the refundable accommodation deposit is treated during life and in the estate, and how the family home fits with it.",
      },
      {
        t: "Enduring powers of attorney first",
        d: "The document families most often need before the will is ever read, prepared and explained to the attorneys who will use it.",
      },
      {
        t: "Signing on the island",
        d: "We travel to Bongaree, Bellara, Woorim and Banksia Beach homes and facilities for will and EPOA signing.",
      },
    ],
    ["Bongaree", "Bellara", "Woorim", "Banksia Beach", "Ningi", "Sandstone Point"],
    ["4507", "4511"],
    [
      {
        q: "Do we have to use a Brisbane firm?",
        a: "No. Wills and enduring powers of attorney are Queensland documents wherever they are signed. We run Bribie files from Caloundra and travel to the island for appointments.",
      },
    ],
  ),
  loc(
    "redcliffe",
    "Redcliffe",
    "Wills, EPOAs and estate advice for Redcliffe families",
    "Fixed-fee wills and enduring powers of attorney for Redcliffe, with a local solicitor who has acted for peninsula families for more than twenty years.",
    [
      "Redcliffe has an older population than most of Moreton Bay, and a high proportion of estates involve a single home, superannuation with a binding or non-binding nomination, and adult children living elsewhere. The difference between a smooth administration and a nine-month argument usually comes down to whether the will was updated after a second relationship, a sale, or a child moving in to care for a parent.",
      "We take the time to ask about those things. If a child has been contributing to the house or providing care, we record it properly rather than leaving it to be argued about later.",
      "Enduring powers of attorney matter just as much here. Once capacity is gone the only alternative is a QCAT application, which costs far more than the document would have.",
      `Many Redcliffe clients have downsized from the family home into a unit along the foreshore, or moved into a retirement village. A will that still gifts "my house" can fail once that house is sold, and a village exit entitlement is paid under the residence contract, not simply sold by the executor. We read those documents when we take instructions.`,
      `Michael Klein lives in Redcliffe and sees clients at our office at ${PENINSULA_ADDRESS}, a short drive from the Redcliffe jetty, or at home, in hospital or in aged care across the peninsula.`,
    ],
    [
      { t: "Local solicitor", d: "Michael Klein, admitted 2003, formerly managing partner of GKS Law on the peninsula." },
      { t: "Fixed fees", d: `Simple will ${WILL_FEES.will}, EPOA ${WILL_FEES.epoa}, pack ${WILL_FEES.pack}, couples ${WILL_FEES.couples}, incl. GST.` },
      { t: "Second relationships", d: "Blended family structures, life interests and mutual will arrangements considered and quoted before we start." },
      { t: "Superannuation", d: "We check nominations, because super often does not pass under the will at all." },
    ],
    ["Scarborough", "Margate", "Woody Point", "Clontarf", "Kippa-Ring", "Rothwell"],
    ["4020", "4019", "4021"],
    [
      {
        q: "My partner and I have children from earlier relationships — is that a simple will?",
        a: "Usually not. Blended family planning is quoted separately in writing before we begin, because it needs proper structuring rather than a standard form.",
      },
    ],
    "peninsula",
  ),
  loc(
    "newport",
    "Newport",
    "Wills and estate lawyers for Newport",
    `Fixed-fee wills and enduring powers of attorney for Newport households from our Scarborough office, minutes away at ${PENINSULA_ADDRESS}. Simple will ${WILL_FEES.will}, will and EPOA pack ${WILL_FEES.pack}, incl. GST.`,
    [
      "Newport is one of the higher-value parts of the peninsula, with canal-front homes, pontoons and boats. When the home is the largest asset by a long way, how it is owned matters as much as what the will says. A home held as joint tenants passes to the survivor outside the will altogether; held as tenants in common, each share passes under each owner's will.",
      "That is the point where second relationships and adult children from earlier marriages need careful thought. We check the title when we take instructions and talk through whether a life interest, a right to reside or a testamentary trust suits your family better than a straight gift.",
      "Many Newport owners also hold property through a company, trust or self-managed super fund. Those assets are not controlled by the will, so we look at the trust deed, company constitution and super nominations alongside it and quote any structuring work in writing before we start.",
    ],
    [
      { t: "Joint tenancy checked", d: "We look at the title so the will and the way the home is owned work together." },
      { t: "Blended families", d: "Life interests, rights to reside and testamentary trusts considered, and quoted separately before we begin." },
      { t: "Trusts, companies and super", d: "We review what sits outside the will so the whole estate plan lines up." },
      { t: "Local solicitor", d: "Michael Klein, admitted 2003, lives in Redcliffe and sees Newport clients at home or at our Scarborough office." },
    ],
    ["Scarborough", "Kippa-Ring", "Redcliffe", "Rothwell", "Deception Bay"],
    ["4020"],
    [
      {
        q: "Does my will control our canal home?",
        a: "Only if you own your share as tenants in common. If you and your partner are joint tenants, the home passes to the survivor automatically, whatever your will says. We check the title and explain which applies before you sign.",
      },
    ],
    "peninsula",
  ),
  loc(
    "margate",
    "Margate",
    "Wills and enduring powers of attorney for Margate and Woody Point",
    "Fixed-fee wills, EPOAs and estate advice for Margate, Woody Point and Clontarf, with home and hospital visits across the peninsula.",
    [
      "Margate sits between the older Woody Point housing stock and the newer unit developments along the foreshore. Many clients here are downsizers who sold a family home elsewhere and now hold a unit, a term deposit and superannuation.",
      "Body corporate units bring their own issues on death: levies keep running, insurance needs to be confirmed and the committee needs to know who to deal with. We set the will up so the executor is not fighting the scheme while trying to sell.",
      "If you are appointing an attorney, we go through what they can and cannot do with the unit while you are alive, including whether they may sell it.",
    ],
    [
      { t: "Unit owners", d: "Levies, insurance and body corporate notification handled as part of the estate advice." },
      { t: "Fixed fees", d: `Simple will ${WILL_FEES.will}, EPOA ${WILL_FEES.epoa}, pack ${WILL_FEES.pack}, incl. GST.` },
      { t: "Home visits", d: "Appointments at home, in hospital or at your village unit across Margate, Woody Point and Clontarf." },
      { t: "Attorney powers explained", d: "We spell out in writing what your attorney may and may not do with your property." },
    ],
    ["Woody Point", "Clontarf", "Scarborough", "Redcliffe", "Newport"],
    ["4019", "4020"],
    [
      {
        q: "Does my unit pass under my will?",
        a: "Only your share, and only if it is held as tenants in common. If it is held as joint tenants it passes automatically to the other owner. We check the title before drafting.",
      },
    ],
    "peninsula",
  ),
  loc(
    "north-lakes",
    "North Lakes",
    "Wills, EPOAs and estate planning for North Lakes families",
    "Fixed-fee wills and enduring powers of attorney for North Lakes, Mango Hill, Murrumba Downs and Griffin — appointments after hours and at your home.",
    [
      "North Lakes is a young suburb with a mortgage-heavy demographic: families in their thirties and forties, both parents working, often with a self-managed super fund or an investment property in the mix. Wills here are less about aged care and more about guardianship of children, life insurance and making sure a mortgaged house does not land on a beneficiary as a debt.",
      "We deal with the questions that actually matter for this group. Who raises the children if both parents die. Whether life insurance is inside super and who it is nominated to. Whether an investment property should be sold or held. Whether a young beneficiary should inherit at 18 or later.",
      "For families with a company, trust or SMSF, we quote that work separately rather than pretending it fits a standard will.",
    ],
    [
      { t: "Guardianship", d: "Appointment of guardians for young children, with substitutes, and practical advice on discussing it first." },
      { t: "Fixed fees", d: `Simple will ${WILL_FEES.will}, EPOA ${WILL_FEES.epoa}, pack ${WILL_FEES.pack}, couples pack ${WILL_FEES.couples}, incl. GST.` },
      { t: "Age of inheritance", d: "Staged distributions so a beneficiary does not receive a lump sum at 18." },
      { t: "Super and insurance", d: "Nomination checks so life cover ends up where you intend, not in the wrong hands." },
    ],
    ["Mango Hill", "Murrumba Downs", "Griffin", "Kallangur", "Dakabin", "Rothwell"],
    ["4509", "4503", "4501"],
    [
      {
        q: "We have a mortgage and young children — what should our will cover?",
        a: "Guardianship, staged inheritance ages, who pays the mortgage out of what, and where your life insurance and super are directed. Those four points cover most of what goes wrong for young families.",
      },
      {
        q: "Do you see clients in North Lakes?",
        a: `Yes. Michael Klein works from our peninsula office at ${PENINSULA_ADDRESS} and sees North Lakes, Mango Hill and Murrumba Downs clients at home or after hours by arrangement.`,
      },
    ],
    "peninsula",
  ),
  loc(
    "kallangur",
    "Kallangur",
    "Wills and enduring powers of attorney for Kallangur and Murrumba Downs",
    "Fixed-fee wills, EPOAs and estate advice for Kallangur, Murrumba Downs, Dakabin and Petrie families.",
    [
      "Kallangur and the suburbs along the rail line hold a mix of long-term owners and newer buyers. Estates here are typically a house, superannuation and modest savings — the sort of estate that is simple to administer when the paperwork is right, and painful when the only will is a newsagency form kit signed without witnesses.",
      "We also see a steady stream of enduring power of attorney work, often when a parent's health changes quickly. Getting the document signed while capacity is clear avoids a QCAT application later.",
      "If an existing will has been damaged, stapled over or written on, bring it in. Alterations and staple holes create real problems on a probate application, and they are fixable while the will maker is alive.",
    ],
    [
      { t: "Fixed fees", d: `Simple will ${WILL_FEES.will}, EPOA ${WILL_FEES.epoa}, pack ${WILL_FEES.pack}, incl. GST.` },
      { t: "Urgent EPOAs", d: "Where health is changing we prioritise the enduring power of attorney and deal with the will next." },
      { t: "Existing wills reviewed", d: "We check alterations, staple marks and witnessing on any will you already hold." },
      { t: "Safe custody", d: "Free storage of your original will, with a copy for your executor." },
    ],
    ["Murrumba Downs", "Dakabin", "Petrie", "North Lakes", "Lawnton"],
    ["4503", "4505", "4502"],
    [
      {
        q: "Is a kit will from the newsagency valid?",
        a: "It can be, but a large share of the problems we see on probate applications start with kit wills — poor witnessing, alterations and unclear gifts. The fixed fee for a proper will is less than the cost of fixing one after death.",
      },
    ],
    "peninsula",
  ),
];


export function getWillsLocation(slug: string): WillsLocation | undefined {
  return WILLS_LOCATIONS.find((l) => l.slug === slug);
}
